export const profile = {
  name: "Sherxon",
  headline: "builds things & mentors people",
  bio: "Software engineer building mobile and web apps that solve real problems. Passionate about mentoring, teaching, and shipping products fast.",
  interests: ["TypeScript", "Rust", "Expo", "Convex", "React", "Elixir"],
  socials: [
    { label: "GitHub", href: "https://github.com/worldwalker", icon: "github" },
    { label: "LinkedIn", href: "https://www.linkedin.com/in/sherkhon-mamatkulov-a34834156/", icon: "linkedin" },
    { label: "Telegram", href: "https://t.me/sherxon_dev_talks", icon: "telegram" },
  ],
} as const;

export type AppFeature = {
  title: string;
  description: string;
  eyebrow?: string;
};

export type AppDetailNote = {
  label: string;
  value: string;
};

export type AppDistributionChannel = {
  label?: string;
  href?: string;
  statusLabel?: string;
};

export type AppMeta = {
  title: string;
  description: string;
  canonicalPath?: string;
};

export type AppOwnership = "personal" | "contributed";

export type AppProject = {
  slug: string;
  name: string;
  tagline: string;
  description: string;
  tags: string[];
  featured: boolean;
  ownership?: AppOwnership;
  routePath?: string;
  reloadDocument?: boolean;
  /** iOS App Store product URL — shown with Apple icon when set */
  appStoreUrl?: string;
  /** Google Play listing URL — shown with play icon when set */
  playStoreUrl?: string;
  /** Public web app or marketing site — shown with globe icon when set */
  webUrl?: string;
  meta?: AppMeta;
  hero?: {
    eyebrow?: string;
    title: string;
    summary: string;
    supporting: string[];
    signals?: string[];
  };
  features?: AppFeature[];
  detailNotes?: AppDetailNote[];
  closingNote?: string;
  distribution?: {
    appStore?: AppDistributionChannel;
    playStore?: AppDistributionChannel;
    web?: AppDistributionChannel;
  };
};

export const apps: AppProject[] = [
  {
    slug: "now-i-understand",
    name: "Now I Understand",
    tagline: "Interactive Node.js execution lab for seeing JavaScript and TypeScript move through code, CPU, RAM, stack, and garbage collection.",
    description:
      "Now I Understand is a bilingual learning app for beginner JavaScript and TypeScript students. It visualizes how Node.js reads code, allocates variables, calls functions, updates memory, prints output, and marks unreachable values for garbage collection.",
    tags: ["JavaScript", "TypeScript", "Node.js", "Education"],
    featured: true,
    ownership: "personal",
    routePath: "/apps/now-i-understand",
    reloadDocument: true,
    meta: {
      title: "Now I Understand | Visual JavaScript and TypeScript Execution in Node.js",
      description:
        "Learn how JavaScript and TypeScript code executes in Node.js with a visual CPU, RAM, call stack, heap, garbage collector, and VS Code-style code editor.",
      canonicalPath: "/apps/now-i-understand",
    },
  },
  {
    slug: "getlief",
    name: "GetLief",
    tagline: "Carbon-neutral travel companion that rewards lighter baggage and lower-emission flying habits.",
    description:
      "GetLief is a live travel app from EcoFlite focused on air-passenger carbon-neutral practice. Travelers upload their e-ticket and baggage information, calculate emissions, and earn points for lighter travel choices.",
    tags: ["Travel", "React Native", "Mobile", "Rewards"],
    featured: true,
    ownership: "contributed",
    routePath: "/apps/getlief",
    appStoreUrl: "https://apps.apple.com/app/id6738381468",
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.ecoflite",
    webUrl: "https://get-lief.com/",
    meta: {
      title: "GetLief | Sherxon",
      description:
        "GetLief is a carbon-neutral travel companion for lighter baggage, ticket upload, carbon calculation, and point rewards.",
      canonicalPath: "/apps/getlief",
    },
  },
  {
    slug: "nechipul",
    name: "Nechipul",
    tagline: "Mobile personal finance tracking for income, expenses, goals, loans, receipts, and daily history.",
    description:
      "Nechipul is a mobile personal finance app built for practical daily logging. It focuses on recording real money movement with enough context to review balances, compare periods, and keep personal bookkeeping grounded.",
    tags: ["Expo", "Convex", "Finance", "iOS", "Android"],
    featured: true,
    ownership: "personal",
    meta: {
      title: "Nechipul | Sherxon",
      description:
        "Nechipul is a mobile personal finance app for tracking income, expenses, goals, loans, receipts, and voice transaction drafts.",
      canonicalPath: "/apps/nechipul",
    },
    hero: {
      eyebrow: "Mobile app",
      title: "Track every income and outcome with precision.",
      summary:
        "A trust-first public home for a mobile finance product built around fast entry, useful history, and clear ownership of personal records.",
      supporting: [
        "Nechipul is built for iPhone and Android with Google sign-in and a focused mobile workflow for recording income and expenses in seconds.",
        "Beyond simple entries, it covers receipts, voice transaction drafts, goals, loans, recent activity, and monthly or yearly comparisons without pretending to be a bank sync dashboard.",
      ],
      signals: ["iPhone + Android", "Google sign-in", "Store links coming soon"],
    },
    features: [
      {
        eyebrow: "Capture",
        title: "Dedicated income and expense flows",
        description:
          "Log amount, currency, and transaction type from mobile-first forms designed for quick daily updates.",
      },
      {
        eyebrow: "Review",
        title: "History and comparison views",
        description:
          "See recent activity and compare income or outcome across monthly and yearly periods from the same product.",
      },
      {
        eyebrow: "Plan",
        title: "Goals and loan tracking",
        description:
          "Track savings goals and loan balances alongside ordinary transactions instead of splitting them into separate tools.",
      },
      {
        eyebrow: "Receipts",
        title: "Camera and image-based receipt capture",
        description:
          "Import or capture receipt images directly from the app to keep spending records tied to source material.",
      },
      {
        eyebrow: "Voice",
        title: "Voice transaction drafts",
        description:
          "Record a short voice note, generate a draft, and continue into the appropriate income or expense flow with context preserved.",
      },
      {
        eyebrow: "Currency",
        title: "Multi-currency entry with FX-aware balances",
        description:
          "Use currency pickers and historical FX handling so balances and comparisons stay closer to what actually happened.",
      },
    ],
    detailNotes: [
      {
        label: "Platforms",
        value: "Native mobile experience for iPhone and Android.",
      },
      {
        label: "Authentication",
        value: "Google sign-in only.",
      },
      {
        label: "Core scope",
        value: "Income, expense, goals, loans, receipts, transaction history, and stats.",
      },
      {
        label: "Public status",
        value: "This page is the public app home for OAuth and store-console identity. Store listing links are not published here yet.",
      },
    ],
    closingNote:
      "Nechipul’s public home exists here so the product has a stable, reviewable web presence while mobile distribution links are still being finalized.",
    distribution: {
      appStore: {
        statusLabel: "Coming soon",
      },
      playStore: {
        statusLabel: "Coming soon",
      },
    },
  },
];

export function getAppBySlug(slug: string): AppProject | undefined {
  return apps.find((app) => app.slug === slug);
}

export type BlogPost = {
  slug: string;
  title: string;
  summary: string;
  tags?: string[];
};

/** Add posts here; empty shows “Soon” on the Blog page. */
export const blogs: BlogPost[] = [];

export function getBlogBySlug(slug: string): BlogPost | undefined {
  return blogs.find((blog) => blog.slug === slug);
}
